import { useCallback } from 'react'

import SubscriptionEvents from 'Pods/Shared/Constants/subscriptionEvents'
import useTracker from 'Pods/Shared/Hooks/useTracker'

const PAGE_TYPES = {
  checkout: 'subscription_checkout',
  update: 'subscription_update_payment',
  upgrade: 'subscription_upgrade',
}

const SDK_METHOD_MAP = {
  googlePay: 'google_pay',
  applePay: 'apple_pay',
}

const resolvePageType = ({ isUpgradeMode, isUpdateMode }) => {
  if (isUpgradeMode) return PAGE_TYPES.upgrade
  if (isUpdateMode) return PAGE_TYPES.update
  return PAGE_TYPES.checkout
}

const useCheckoutTracking = ({ isUpdateMode = false, isUpgradeMode = false, tier = null } = {}) => {
  const { trackEvent } = useTracker()
  const pageType = resolvePageType({ isUpgradeMode, isUpdateMode })
  const bannerEntry = typeof sessionStorage !== 'undefined'
    && sessionStorage.getItem('subscription_banner_entry') === 'true'

  // Upgrades always target premium; the passed tier is the user's current (essentials) tier,
  // which mislabelled upgrade conversions as essentials. Fall back to essentials so the tier
  // param is never blank.
  const effectiveTier = isUpgradeMode ? 'premium' : (tier || 'essentials')

  const viewEventName = isUpdateMode
    ? SubscriptionEvents.UPDATE_PAYMENT_VIEWED
    : SubscriptionEvents.CHECKOUT_VIEWED

  // Payment-method updates are not new subscriptions, so they get their own success event to
  // keep them out of the subscription_payment_success conversion count.
  const successEventName = isUpdateMode
    ? SubscriptionEvents.UPDATE_PAYMENT_SUCCESS
    : SubscriptionEvents.PAYMENT_SUCCESS

  const trackView = useCallback(() => {
    trackEvent(viewEventName, { page_type: pageType, subscription_tier: effectiveTier })
  }, [trackEvent, viewEventName, pageType, effectiveTier])

  const trackPayClick = useCallback(() => {
    trackEvent(SubscriptionEvents.PAY_CLICK, { page_type: pageType, subscription_tier: effectiveTier })
  }, [trackEvent, pageType, effectiveTier])

  const trackSuccess = useCallback(() => {
    trackEvent(successEventName, {
      page_type: pageType,
      subscription_tier: effectiveTier,
      ...(bannerEntry && { banner_entry: true }),
    })
  }, [trackEvent, successEventName, pageType, effectiveTier, bannerEntry])

  const trackPaymentMethodSelection = useCallback((method) => {
    const mappedMethod = SDK_METHOD_MAP[method] || method
    trackEvent(SubscriptionEvents.PAYMENT_METHOD_SELECTED, {
      method: mappedMethod,
      page_type: pageType,
      subscription_tier: effectiveTier,
    })
  }, [trackEvent, pageType, effectiveTier])

  const trackFailure = useCallback((errorMessage) => {
    trackEvent(SubscriptionEvents.PAYMENT_FAILURE, {
      page_type: pageType,
      subscription_tier: effectiveTier,
      error_message: errorMessage,
    })
  }, [trackEvent, pageType, effectiveTier])

  return {
    trackView,
    trackPayClick,
    trackSuccess,
    trackPaymentMethodSelection,
    trackFailure,
  }
}

export default useCheckoutTracking
